import * as React from "react";

// Material-UI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import Grid from '@mui/material/Grid';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import ClubEmailSelect from './ClubEmailSelect';
import MemberEmailHelpDialog from './MemberEmailHelpDialog';
import RichTextEditor from '../../shared/RichTextEditor';

import {
  ClubEmailAddress,
  EmailMessage,
  MemberEmailMessage
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  NULL_ID
} from '../../global';

import {
  EmailAttachment,
  HTTPHeaders,
  RichTextEditorRef
} from '../../interfaces';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';
// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface MemberEmailProps {
  clear: () => void;
  showFilter: () => void;
  statusFilter: Array<number>;
  totalSelected: number;
}

const MemberEmailForm: React.FC<MemberEmailProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { isMobile } = useResponsive();
  const { enqueueSnackbar } = useSnackbar();

  const [clubEmailAddress, setClubEmailAddress] = React.useState<ClubEmailAddress>(new ClubEmailAddress());
  const [emailMessage, setEmailMessage] = React.useState<EmailMessage>(new EmailMessage());
  const [helpOpen, setHelpOpen] = React.useState<boolean>(false);
  const [sendDisabled, setSendDisabled] = React.useState<boolean>(true);
  const [showWorking, setShowWorking] = React.useState<boolean>(false);

  // Ensures the editor only sets the starting text once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialContent = React.useMemo(() => emailMessage.htmlContent, []);

  const editorRef = React.useRef<RichTextEditorRef>(null);
  //const senderRef = React.useRef<ClubEmailSelectRef>(null);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1
    },
    actions: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    subject: {
      flexGrow: 1,
      margin: theme.spacing(1, 1, 1, 1),
      width: '100%',
    },
    editorWrapper: {
      width: '100%',
      marginTop: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden' // Keeps the editor corners clean
    },
  };

  // #endregion

  // #region Methods

  /**
   * Resets the entire email composition form to its initial state.
   * This clears the local message state, the rich text editor (including attachments),
   * and notifies the parent component to perform any additional cleanup.
   */
  const clearEmail = () => {

    // Reset the local state object containing the email details (subject, body, etc.)
    setEmailMessage(new EmailMessage());

    // Reset the "From" dropdown
    setClubEmailAddress(new ClubEmailAddress());
    //senderRef.current?.reset();

    // Access the child component's imperative 'reset' method via the ref.
    // This clears the TinyMCE editor content, the attachment list, and the file input.
    editorRef.current?.reset();

    // Execute the 'clear' callback passed from the parent component
    // to handle high-level state cleanup (e.g., closing a modal or clearing shared state).
    props.clear();
  };

  /**
   * Orchestrates the email submission process.
   * Gathers data from the editor ref, transforms attachments into a comma-separated string,
   * and posts the final payload to the .NET API.
   */
  const sendEmail = async () => {
    // Start loading indicator
    setShowWorking(true);

    // 1. Retrieve Auth0 bearer token for secure API access
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const url = new URL(`${API_URL}/v1/email/members`);

    // 2. Build the payload for the Member Email API
    const memberEmailMessage: MemberEmailMessage = new MemberEmailMessage();
    memberEmailMessage.filter.status = props.statusFilter.join(',');
    memberEmailMessage.subject = emailMessage.subject;
    memberEmailMessage.htmlContent = emailMessage.htmlContent;
    memberEmailMessage.clubEmailAddress = clubEmailAddress;

    // 3. Extract attachments from the RichTextEditor via the imperative ref.
    // We map the array of objects into a single comma-separated string of IDs for the backend.
    const emailAttachments = editorRef.current?.getAttachments();
    if (emailAttachments && emailAttachments.length > 0) {
      let attachments: string = EMPTY_STRING;
      emailAttachments.forEach((ea: EmailAttachment) => {
        attachments += `${attachments ? ',' : EMPTY_STRING}${ea.emailAttachmentID}`
      });
      memberEmailMessage.attachments = attachments;
    }

    // 4. Dispatch the request to the .NET backend
    await fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(memberEmailMessage),
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          enqueueSnackbar(`WARNING: Member email was not sent`, { variant: 'warning' });
        } else {
          // 5. On success: Notify user and trigger the full form reset
          enqueueSnackbar(`Email sent to ${props.totalSelected} members from ${clubEmailAddress.entityName}`, { variant: 'success' });
          clearEmail();
        }
        setShowWorking(false);
      })
      .catch(error => {
        // Handle network errors or unexpected API crashes
        setShowWorking(false);
        enqueueSnackbar(`WARNING: Member email was not sent, Error: ${error}`, { variant: 'error' });
      });
  }

  /**
   * Validates the editor content to ensure it isn't just empty tags or whitespace.
   * TinyMCE often leaves behind empty <p></p> tags or &nbsp; entities.
   */
  const validateContent = () => {
    if (!emailMessage.htmlContent) return false;

    // 1. Strip HTML tags using regex (e.g., <p>Hello</p> -> Hello)
    let plainText = emailMessage.htmlContent.replace(/<[^>]*>/g, '');

    // 2. Replace common HTML entities like &nbsp; with a standard space
    // This prevents an email consisting only of spaces from passing validation.
    plainText = plainText.replace(/&nbsp;/gi, ' ').trim();

    // 3. Return true only if there is actual visible character content remaining
    return plainText.length > 0;
  };

  /**
   * Evaluates the form's state to determine if it is "Ready to Send."
   * This checks for a valid recipient filter, a sender address, 
   * a subject line, and actual body content.
   */
  const validateEmail = () => {
    // We only enable the 'Send' button if ALL of the following conditions are met:
    if (
      // 1. Ensure at least one status (e.g., 'Active', 'Pending') is selected
      props.statusFilter.length > 0 &&

      // 2. Ensure a valid sender email address has been selected from the dropdown
      clubEmailAddress.clubEmailAddressID !== NULL_ID &&

      // 3. Ensure the subject line is not empty or null
      emailMessage.subject &&

      // 4. Run the helper function to ensure the body contains actual text,
      // not just empty HTML tags like <p></p>
      validateContent()
    ) {
      // All checks passed: Enable the button
      setSendDisabled(false);
    } else {
      // One or more checks failed: Keep the button disabled
      setSendDisabled(true);
    }
  };

  // #endregion

  // #region Event handlers

  /*
   * Handle changes to club email address
   */
  const handleClubEmailSelected = (clubEmail: ClubEmailAddress) => {
    setClubEmailAddress(clubEmail);
  };

  /*
   * Handler for help dialog window
   */
  const handleHelpClose = () => {
    setHelpOpen(false);
  }

  /*
   * Handles editor content change
   */
  const handleEditorChange = (content: string) => {
    setEmailMessage(prev => ({
      ...prev,
      htmlContent: content
    }));
  }

  /*
   * Handle changes to text field
   */
  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setEmailMessage(prev => ({
      ...prev,
      [name]: value
    }));
  };

  React.useEffect(() => {
    validateEmail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailMessage, props.statusFilter, clubEmailAddress.clubEmailAddressID]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container spacing={1} alignItems="center">
        {isMobile &&
          <Grid size={1}>
            <Tooltip title="Filter member emails">
              <IconButton
                onClick={props.showFilter}
                size="large"
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        }
        <Grid size={2}>
          <ClubEmailSelect
            clubEmailAddressID={clubEmailAddress.clubEmailAddressID.toString() }
            clubEmailSelected={handleClubEmailSelected}
          />
        </Grid>
        <Grid size={2}>
          <Tooltip title="Clear all email content">
            <IconButton onClick={clearEmail} size="large">
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Member email help">
            <IconButton onClick={() => setHelpOpen(true)} size="large">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid>
          <Typography>{`Emails: ${props.totalSelected}`}</Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid size={12}>
          <TextField
            name="subject"
            value={emailMessage.subject}
            autoFocus
            label="Subject"
            sx={styles.subject}
            onChange={handleSubjectChange}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid size={12}>
          <Box style={styles.editorWrapper}>
            <RichTextEditor
              ref={editorRef}
              initialValue={initialContent}
              onChange={handleEditorChange}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container sx={styles.actions} justifyContent="flex-end">
        <Grid size={1}>
          {showWorking &&
            <CircularProgress size="30" />
          }
        </Grid>
        <Grid size={2}>
          <Button
            disabled={sendDisabled}
            endIcon={<SendIcon />}
            onClick={sendEmail}
            size="medium"
            variant="contained"
          >
            Send
          </Button>
        </Grid>
      </Grid>
      <MemberEmailHelpDialog show={helpOpen} handleClose={handleHelpClose} />
    </Box>
  );

}

export default MemberEmailForm;